// client/src/hooks/useFetch.js
import { useState, useEffect } from 'react';

// Same-origin path to our function. Lives here, not in App.js, because
// every fetch in the app uses this prefix — it's a config concern, not
// a per-screen concern. If the deployment URL ever changes, we change
// it in one place.
// TODO: env-ify once we set up the GitHub repo. Hardcoded to dev URL for now.
// Production URL will differ when we deploy to production environment.
const API_BASE = 'https://server-dev.bightyouthsports.com/server/ysa-api';

/**
 * Fetches a path under our API and returns { data, loading, error }.
 *
 * Handles three things every endpoint shares:
 *   1. HTTP-level failures (non-2xx responses)
 *   2. Our envelope shape ({ success: true, data } vs { success: false, error })
 *   3. The loading/error/success state machine
 *
 * Usage:
 *   const { data, loading, error } = useFetch('/me');
 *   const { data, loading, error } = useFetch(`/teams/${teamId}`);
 */
export function useFetch(path) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // `cancelled` is a closure-scoped flag for the race condition where
    // the component unmounts (or the path prop changes) before this
    // fetch resolves. Without it, we'd call setState on a stale effect,
    // which React warns about and can cause subtle bugs in real apps.
    // See the gotcha section below for more.
    let cancelled = false;

    // Reset state every time the path changes — if a parent switches us
    // from /teams/1 to /teams/2, we want to show "loading" again, not
    // briefly flash the old data.
    setLoading(true);
    setError(null);

    async function run() {
      try {
        const res = await fetch(`${API_BASE}${path}`, {
        credentials: 'include',
          });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status} ${res.statusText}`);
        }

        const body = await res.json();

        if (!body.success) {
          throw new Error(body.error || 'Unknown server error');
        }

        // Only commit state if this effect's run is still the active one.
        if (!cancelled) setData(body.data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();

    // The cleanup function. React calls this when:
    //   (a) the component unmounts, or
    //   (b) the effect is about to re-run (because `path` changed).
    // Either way, we flip the flag so the in-flight fetch's setState
    // calls become no-ops.
    return () => {
      cancelled = true;
    };
  }, [path]); // ← effect re-runs whenever `path` changes

  return { data, loading, error };
}